import type {Agency} from 'src/api-parsers/index.types';
import type {Dispatch} from 'src/reducers';
import type {RouteLocation} from 'src/types/router';
import IndexStore from 'src/stores/index';
import {setAIPanelOpenEvent} from 'src/action-creators/sense-ai-panel';

import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import * as React from 'react';

import * as reduxApi from 'src/utils/redux-api-v2';
import {usePageTitle} from 'src/contexts/page-title-context';
import {connect, useSelector, useDispatch} from 'react-redux';
import flow from 'lodash/flow';
import {provideHooks} from 'src/flux/provideHooks';
import {withWindowWidth} from 'src/flux/responsive';
import {fluxify} from 'src/flux/component';
import {setAuthPath} from 'src/actions';
import useI18n from 'src/hooks/useI18n';
import {getAgency} from 'src/action-creators/agency';
import {getAllEntities} from 'src/action-creators/ats-entities';
import {classify} from 'src/utils';
import {isMobile} from 'src/utils/responsive';
import {getFromAuthedUser, hasPrivilege} from 'src/utils/accounts';
import {selectDisplayPhoneNumber, agentHasPhone} from 'src/selectors/chat';
import {selectCurrentPhoneCountry} from 'src/selectors/phone-number-sets';
import {selectFullscreen} from 'src/selectors/app-config';
import {setSideNavExpanded} from 'src/action-creators/app-config';

import {RouterErrorBoundary} from 'src/components/500/boundary';
import Loader from 'src/components/navigation/loader';
import Notification from 'src/components/navigation/notification';
import BadBrowserNotification from 'src/components/navigation/bad-browser-notification';
import MessagesSocket from 'src/components/messaging/socket';
import {PageTitle} from 'src/components/app-shell/page-title';

import {selectTotalUnreadThreadCount} from 'src/selectors/messages';
import {useWindowSize} from '@spaced-out/ui-design-system/lib/hooks/useWindowSize';

import AppShellSideNavContainer from 'src/components/app-shell/app-shell-side-nav-container';

import css from 'src/components/navigation/top.css';

import appShellCss from './app-shell-container.css';

import {
  USE_MESSAGING,
  USE_JD_GENERATOR,
  USE_PRESCREENING_GENERATOR,
  USE_RESUME_GENERATOR,
} from 'src/action-creators/roles';

import {
  getAppShellStatus,
  setAppShellStatus,
} from 'src/utils/app-shell-storage';


type OuterProps = {
  children: React.ReactNode;
};

type TopNavigationProps = OuterProps & {
  store: IndexStore;
  authed: boolean;
  agency: Agency;
  name: string | null | undefined;
  dispatch: Dispatch;
  location: RouteLocation;
  params: Record<any, any>;
  groupName: string | null | undefined;
  authedUserPhone: string | null | undefined;
  isMobileWindow: boolean;
  isMobile: boolean;
  context: string;
  currentPhoneCountry: string;
  audienceFilterSeparation?: boolean;
  totalUnreadCount: number;
  canViewChat: boolean;
  setIsAiPanelOpen: (value: boolean) => void;
  showAIPanel: boolean;
  hasSenseAIPermission: boolean;
  handleSenseUniversityOnClick: () => void;
};

const dependencies = {
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  required: ({dispatch}) => dispatch(getAgency()),
  // NOTE (kyle): ats entities are needed almost everywhere in our application.
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  deferred: ({dispatch}) => dispatch(getAllEntities()),
} as const;

const mapStateToProps = (state: any) => {
  return {
    agency: state.agency.agency,
    authed: state.accounts.authedUserId !== null,
    // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
    name: getFromAuthedUser(state, 'name'),
    // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
    groupName: getFromAuthedUser(state, ['securityGroup', 'name']),
    authedUserPhone: selectDisplayPhoneNumber(state),
    isMobile: isMobile(state.requestHeaders.headers['user-agent']),
    context: state.env.query.context,
    currentPhoneCountry: selectCurrentPhoneCountry(state),
    totalUnreadCount: selectTotalUnreadThreadCount(state),
    canViewChat: agentHasPhone(state) && hasPrivilege(state, USE_MESSAGING),
    showAIPanel: state.senseAIPanel.isAiPanelOpen,
    hasSenseAIPermission:
      hasPrivilege(state, USE_JD_GENERATOR) ||
      hasPrivilege(state, USE_PRESCREENING_GENERATOR) ||
      hasPrivilege(state, USE_RESUME_GENERATOR),
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  setIsAiPanelOpen(value: any) {
    dispatch(setAIPanelOpenEvent(value));
  },
  async handleSenseUniversityOnClick() {
    // @ts-expect-error - TS7006 - Parameter 'url' implicitly has an 'any' type.
    await dispatch(reduxApi.get('sense-university/login')).then((url) => {
      window.open(url, '_blank');
    });
  },
});

function AppShellContainer(props: TopNavigationProps) {
  const {
    store,
    location,
    authed,
    children,
    name,
    agency: {activeEventProcessor, slug},
    isMobile,
    context,
    setIsAiPanelOpen,
    handleSenseUniversityOnClick,
  } = props;

  const {pathname} = location;
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();

  const defaultState = true;

  const [stateExpand, setExpand] = React.useState(
    getAppShellStatus() ? false : defaultState,
  );

  const {
    pageTitleState: {tabSlotComponent, hidePageTitle},
  } = usePageTitle();

  React.useEffect(() => {
    setAppShellStatus(stateExpand.toString());
    dispatch(setSideNavExpanded(stateExpand));
  }, [stateExpand]);

  const isFullscreenExperience = useSelector(selectFullscreen);

  const [showMenu, setShowMenu] = React.useState(false);
  const {width} = useWindowSize();
  const {isExtension} = useEnvironmentContext();
  const extensionProductList = [
    '/messages',
    '/contacts',
    '/referralv2',
    '/sense-ai-panel',
    '/meetings',
    '/jobs/all',
  ];
  const isAppSupportedInExtension =
    width > 600 &&
    extensionProductList.some((extPath) => pathname.startsWith(extPath));

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  React.useEffect(() => {
    if (showMenu) {
      const handleBodyClick = () => {
        setShowMenu(false);
      };

      document.addEventListener('click', handleBodyClick);

      return () => {
        document.removeEventListener('click', handleBodyClick);
      };
    }
  }, [showMenu]);

  React.useEffect(() => {
    setAuthPath(store, '/signin');
    const handlePostMessage = (event: any) => {
      if (event?.data?.type === 'CLOSE_AI_PANEL') {
        setIsAiPanelOpen(event.data.showAIPanel);
      }
    };
    window.addEventListener('message', handlePostMessage);
    return () => {
      // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'string'.
      setAuthPath(store, null);
      window.removeEventListener('message', handlePostMessage);
    };
  }, []);

  const topNavHidden =
    context === 'pwa' ||
    context === 'trm-iframe' ||
    context === 'discover-iframe' ||
    context === 'sense-ai' ||
    isMobile ||
    isFullscreenExperience;

  const fullyHidden = isExtension || topNavHidden;

  return (
    <div className={css.container}>
      <div
        className={classify(appShellCss.appShellContainer, {
          [appShellCss.withoutHeader]: topNavHidden,
        })}
      >
        <div className={appShellCss.sideNavigationBar}>
          <AppShellSideNavContainer
            expanded={stateExpand}
            setExpand={setExpand}
            name={name}
            handleSenseUniversityOnClick={handleSenseUniversityOnClick}
          />
        </div>
        <>
          {!topNavHidden && !hidePageTitle && (
            <div className={appShellCss.topNavigationBar}>
              <PageTitle />
            </div>
          )}
          <div
            className={classify(appShellCss.contentArea, {
              [appShellCss.contentAreaWithoutHeader]:
                topNavHidden || hidePageTitle,
              [appShellCss.contentAreaWithTabSlot]: !!tabSlotComponent,
            })}
          >
            {authed &&
              !activeEventProcessor &&
              slug !== 'demo' &&
              process.env.NODE_ENV ===
                'production' /* aka "is production or staging" */ && (
                <Notification className={appShellCss.notification}>
                  <strong>
                    {t(
                      'Your_account_is_in_setup_mode',
                      'Your account is in setup mode,',
                    )}
                  </strong>
                  <span>
                    {`${t(
                      'so_no_automated_messages_will_be_sent',
                      'so no automated messages will be sent.',
                    )} ${t(
                      'To_enable_your_account_and_go_live_please_contact_Sense',
                      'To enable your account and go live, please contact Sense.',
                    )}`}
                  </span>
                </Notification>
              )}
            <BadBrowserNotification />
            <div
              className={classify(appShellCss.child, {
                [appShellCss.extensionResponsiveness]:
                  isExtension || isAppSupportedInExtension,
              })}
            >
              <RouterErrorBoundary>{children}</RouterErrorBoundary>
            </div>
          </div>
        </>
      </div>
      <Loader />
      {/*
          Open WebSocket even if TopNavBar is fully hidden by the chrome-extension.
          TODO (gab): Eventually move socket into main app component (must first decouple
          surveys from main app)
        */}
      <MessagesSocket />
    </div>
  );
}

export default flow(
  withWindowWidth,
  // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type '(arg1: T & { store: IndexStore; params: { [key: string]: string; }; }) => unknown'.
  fluxify({}, {pure: true}),
  connect(mapStateToProps, mapDispatchToProps),
  provideHooks(dependencies),
)(AppShellContainer) as React.ComponentType<OuterProps>;
